import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { resolveActiveLanguage } from '@core/i18n/language';

/**
 * Single source of truth for the **active** language as a signal. Wraps `TranslateService`'s
 * `onLangChange` stream once (one subscription, shared app-wide) instead of each consumer
 * re-deriving it. Use this anywhere a component/service needs to react to the current language —
 * e.g. building localized router links or language-scoped requests.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
    private readonly _translate: TranslateService = inject(TranslateService);

    /** Active language code (`en` / `de`); updates on every language change. */
    // Seed with the *resolved* language (URL/stored/browser), not a blind `de` fallback: before the
    // language settles `getCurrentLang()` is null, and a `de` seed made every `en` page fetch `de`
    // first and then refetch `en` when `onLangChange` fired. Resolving up front avoids that flip.
    public readonly lang: Signal<string> = toSignal(
        this._translate.onLangChange.pipe(map((event: LangChangeEvent): string => event.lang)),
        { initialValue: this._translate.getCurrentLang() ?? resolveActiveLanguage() },
    );

    /**
     * Builds a language-prefixed router-link command array for an app-relative path, e.g.
     * `localizedCommands('about')` → `['/', 'en', 'about']`. The replacement for the legacy
     * `| localize` pipe.
     */
    public localizedCommands(path: string | null | undefined): ReadonlyArray<string> {
        const segments: ReadonlyArray<string> = (path ?? '').split('/').filter(Boolean);

        return ['/', this.lang(), ...segments];
    }
}
